import {
  ActionConfirmationModal,
  Button,
  Form,
  FormSubmitButton,
  SimpleSelectFormField,
  SwitchFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { InsuranceCopyModeModal } from "@/components/features/matter/insurance/InsuranceCopyModeModal";
import { LinkedMatterMembershipPopover } from "@/components/features/matter/shared/LinkedMatterMembershipPopover";
import { RelatedMatterObjectDeleteModal } from "@/components/features/matter/shared/RelatedMatterObjectDeleteModal";
import {
  contactIds,
  createInsuranceFormSchema,
  insuranceDefaultFormValues,
  policyLimitMappings,
} from "@/components/features/matter/insurance/AddOrEditInsuranceModal/insurance-form-config";
import { AddContactFormField } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { MatterContactConnectionModalPreview } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionModalPreview";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterInsurance,
  useSyncInsuranceContactConnections,
  useUpdateMatterInsurance,
} from "@/lib/data/react-query";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCopyPartyInsuranceToRelatedMatters,
  useUnlinkObjectFromMatter,
} from "@/lib/data/react-query/matters/matter-related-object";
import type { InsuranceRelatedMatterPlacement } from "@/lib/data/requests/matters/matter-insurance";
import { queryKeys } from "@/lib/data/static";
import { MATTER_CONTACT_CONNECTION_DEFAULT_VALUES } from "@/lib/data/static/formStatics";
import { MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS } from "@/lib/data/static/matters/insuranceCoverageStatics";
import {
  CATEGORIES_BY_INSURANCE_TYPE,
  INSURANCE_TYPES,
  MATTER_INSURANCE_LIABILITY_OPTIONS,
  type InsuranceType,
} from "@/lib/data/static/matters/insuranceStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type {
  APIError,
  APIResponse,
  Contact,
  Connection,
  MatterContactConnection,
  MatterInsurance,
  MatterInsuranceData,
} from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { cn } from "@/lib/utils/cn";
import {
  createInsuranceContactConnectionChildInput,
  getInsuranceConnectionFormContact,
  getInsuranceConnectionPreviewContact,
  getInsuranceContactConnection,
  getInsuranceContactConnections,
  getDesiredInsuranceContactIdsForSingleSlot,
  type SupportedInsuranceContactRole,
} from "@/lib/utils/connections/insurance-contact-connections";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { getRelatedMatterSelectionOptions } from "@/lib/utils/relatedMatters";
import {
  compareMatterDefendantConnectionsDisplayOrder,
  getlabel,
  getMatterContactConnections,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useLocation } from "@tanstack/react-router";
import { type AxiosError } from "axios";
import { isNil, omit, omitBy } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import type { z } from "zod";

interface AddOrEditInsuranceModalProps {
  description?: string;
  editing: boolean;
  insuranceId?: string;
  matterId: string;
  open: boolean;
  setEditing?: (editing: boolean) => void;
  setOpen: (open: boolean) => void;
  setSelectedInsuranceId?: (insuranceId: string) => void;
  title: string;
  afterCreateFn?: (newInsurance: APIResponse<MatterInsuranceData>) => void;
  enforceRequiredContact?: "Client" | "Defendant";
  requiredInsurancePartyContactId?: string;
  initialData?: Partial<MatterInsurance>;
  defendantContactIdContext?: string;
  insurancePartyRole?: "Client" | "Defendant" | "OtherParty";
  sourceMatterContactConnectionId?: string;
}

function insuranceMatterContactRolePreview(
  contact: Contact,
  matterId: string,
  label: string,
  storedConnection: Connection | undefined,
  onRemoveFromForm?: () => void,
) {
  const matchingStoredConnection =
    storedConnection?.targetObjectId === contact.id
      ? storedConnection
      : undefined;
  const previewContact =
    getInsuranceConnectionPreviewContact(matchingStoredConnection) ?? contact;

  return (
    <MatterContactConnectionModalPreview
      contactId={contact.id}
      previewContact={previewContact}
      fetchContactOnEdit={Boolean(matchingStoredConnection)}
      matterId={matterId}
      visualVariant={label === "Insurer" ? "hero" : "white"}
      matterContactConnection={
        {
          contact: previewContact,
          contactId: contact.id,
          label,
        } as MatterContactConnection
      }
      onRemoveContact={
        onRemoveFromForm
          ? () => {
              onRemoveFromForm();
            }
          : undefined
      }
    />
  );
}

export const AddOrEditInsuranceModal = ({
  title,
  editing,
  insuranceId,
  matterId,
  open,
  setOpen,
  setSelectedInsuranceId,
  afterCreateFn,
  enforceRequiredContact,
  requiredInsurancePartyContactId,
  initialData,
  defendantContactIdContext,
  insurancePartyRole,
  sourceMatterContactConnectionId,
}: AddOrEditInsuranceModalProps) => {
  const { hasActionPermission } = useUser();
  const { updateSlideoverPayload } = useModals();
  const queryClient = useQueryClient();

  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const insurance = matter.insurances.find(
    (insurance) => insurance.id === insuranceId,
  );
  const insuranceInitialValues = mergeInitialData(insurance, initialData);
  const contactConnections = getMatterContactConnections(matter);

  const clientConnection = contactConnections.find(
    (connection) => getlabel(connection) === "Client",
  );
  const defendantConnection = useMemo(
    () =>
      contactConnections
        .filter((connection) => getlabel(connection) === "Defendant")
        .sort(compareMatterDefendantConnectionsDisplayOrder),
    [contactConnections],
  );

  const clientContact = clientConnection ? clientConnection.contact : undefined;
  const requiredInsurancePartyContact = useMemo(() => {
    const requiredPartyContactId = requiredInsurancePartyContactId?.trim();
    if (!requiredPartyContactId) {
      return undefined;
    }
    return contactConnections.find(
      (connection) => connection.contactId === requiredPartyContactId,
    )?.contact;
  }, [contactConnections, requiredInsurancePartyContactId]);

  const defendantContacts = useMemo(
    () => defendantConnection.map((connection) => connection.contact),
    [defendantConnection],
  );

  const defendantsForAssign = useMemo(() => {
    if (!defendantContactIdContext) {
      return defendantContacts;
    }
    const fromList = defendantContacts.find(
      (contact) => contact.id === defendantContactIdContext,
    );
    return fromList ? [fromList] : [];
  }, [defendantContactIdContext, defendantContacts]);

  const defendantContactIdsForSchema = defendantContactIdContext
    ? [defendantContactIdContext]
    : defendantContacts.map((defendant) => defendant.id);

  const insuredConnections = getInsuranceContactConnections(
    insurance,
    "Insured",
  );
  const insurer = getInsuranceContactConnection(insurance, "Insurer");
  const policyHolder = getInsuranceContactConnection(
    insurance,
    "Policy Holder",
  );
  const bodilyInjuryAdjuster = getInsuranceContactConnection(
    insurance,
    "Bodily Injury Adjuster",
  );
  const propertyDamageAdjuster = getInsuranceContactConnection(
    insurance,
    "Property Damage Adjuster",
  );
  const medPayAdjuster = getInsuranceContactConnection(
    insurance,
    "MedPay Adjuster",
  );
  const adjusterSupervisor = getInsuranceContactConnection(
    insurance,
    "Adjuster Supervisor",
  );
  const subrogationVendor = getInsuranceContactConnection(
    insurance,
    "Subrogation Vendor",
  );
  const subrogationRepresentative = getInsuranceContactConnection(
    insurance,
    "Subrogation Representative",
  );

  const insuranceFormSchema = createInsuranceFormSchema(
    enforceRequiredContact,
    clientContact?.id,
    defendantContactIdsForSchema,
    requiredInsurancePartyContactId,
  );
  const editingInsuranceValues = insuranceInitialValues ?? insurance;

  /**
   * Matter snapshot for form reset when opening the shell — not wired as RHF `values`.
   * `useUpdateMatterInsurance` optimistically patches `matterInsurance` in cache on mutate;
   * if the form were controlled from that snapshot while `matterContactConnection` is still
   * stale, RHF would re-sync the whole form and wipe in-progress contact picks.
   */
  const getFormValuesFromMatterSnapshot = (): z.infer<
    typeof insuranceFormSchema
  > => {
    if (editing && editingInsuranceValues) {
      return {
        ...insuranceDefaultFormValues,
        ...omitBy(editingInsuranceValues, isNil),
        medPay:
          editingInsuranceValues.medPay ??
          editingInsuranceValues.medPayLimit != null,
        umExists:
          editingInsuranceValues.umExists ??
          (editingInsuranceValues.umPerPerson != null ||
            editingInsuranceValues.umPerIncident != null),
        uimExists:
          editingInsuranceValues.uimExists ??
          (editingInsuranceValues.uimPerPerson != null ||
            editingInsuranceValues.uimPerIncident != null),
        matterContactConnectionInsured: insuredConnections
          .map(getInsuranceConnectionFormContact)
          .filter((contact) => contact !== undefined),
        matterContactConnectionInsurer:
          getInsuranceConnectionFormContact(insurer),
        matterContactConnectionPolicyHolder:
          getInsuranceConnectionFormContact(policyHolder),
        matterContactConnectionBodilyInjuryAdjuster:
          getInsuranceConnectionFormContact(bodilyInjuryAdjuster),
        matterContactConnectionPropertyDamageAdjuster:
          getInsuranceConnectionFormContact(propertyDamageAdjuster),
        matterContactConnectionMedPayAdjuster:
          getInsuranceConnectionFormContact(medPayAdjuster),
        matterContactConnectionAdjusterSupervisor:
          getInsuranceConnectionFormContact(adjusterSupervisor),
        matterContactConnectionSubrogationVendor:
          getInsuranceConnectionFormContact(subrogationVendor),
        matterContactConnectionSubrogationRepresentative:
          getInsuranceConnectionFormContact(subrogationRepresentative),
        policyLimitCombined:
          !editingInsuranceValues.policyLimitPerPerson &&
          !editingInsuranceValues.policyLimitPerIncident
            ? ""
            : (Object.keys(policyLimitMappings).find((key) => {
                const valueObj =
                  policyLimitMappings[key as keyof typeof policyLimitMappings];
                if (
                  valueObj.person ===
                    editingInsuranceValues.policyLimitPerPerson &&
                  valueObj.incident ===
                    editingInsuranceValues.policyLimitPerIncident
                ) {
                  return key;
                }
              }) ?? "Other"),
      };
    }
    return {
      ...insuranceDefaultFormValues,
      ...omitBy(initialData ?? {}, isNil),
      matterContactConnectionInsured: [],
    };
  };

  const matterSnapshotFormValuesRef = useRef(getFormValuesFromMatterSnapshot);
  matterSnapshotFormValuesRef.current = getFormValuesFromMatterSnapshot;

  const insuranceForm = useForm<z.infer<typeof insuranceFormSchema>>({
    resolver: zodResolver(insuranceFormSchema),
    defaultValues: getFormValuesFromMatterSnapshot(),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const createInsurance = useCreateMatterInsurance({ showToast: false });
  const updateInsurance = useUpdateMatterInsurance({ showToast: false });
  const unlinkInsurance = useUnlinkObjectFromMatter({
    objectLabel: "Insurance",
  });
  const syncInsuranceConnections = useSyncInsuranceContactConnections();
  const copyPartyInsurance = useCopyPartyInsuranceToRelatedMatters({
    showToast: false,
  });
  const [isSubmittingInsurance, setIsSubmittingInsurance] = useState(false);

  const isFormLoading =
    isSubmittingInsurance ||
    createInsurance.isPending ||
    updateInsurance.isPending ||
    unlinkInsurance.isPending ||
    syncInsuranceConnections.isPending ||
    copyPartyInsurance.isPending;

  const [, setSelectedInsurer] = useState<Contact | null>(null);
  const [, setSelectedPolicyHolder] = useState<Contact | null>(null);
  const [, setSelectedBodilyInjuryAdjuster] = useState<Contact | null>(null);
  const [, setSelectedPropertyDamageAdjuster] = useState<Contact | null>(null);
  const [selectedMedPayAdjuster, setSelectedMedPayAdjuster] =
    useState<Contact | null>(null);
  const [, setSelectedAdjusterSupervisor] = useState<Contact | null>(null);
  const [, setSelectedSubrogationVendor] = useState<Contact | null>(null);
  const [, setSelectedSubrogationRepresentative] = useState<Contact | null>(
    null,
  );

  const [isDeleting, setIsDeleting] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const watchedInsuredContacts = insuranceForm.watch(
    "matterContactConnectionInsured",
  );
  const currentInsuredContactIds = watchedInsuredContacts.map(
    (contact) => contact.id,
  );
  const defaultAssignableInsured = useMemo(() => {
    if (requiredInsurancePartyContact) {
      return requiredInsurancePartyContact;
    }
    if (enforceRequiredContact === "Client" && clientContact) {
      return clientContact;
    }
    if (defendantsForAssign.length === 1) {
      return defendantsForAssign[0];
    }
    return undefined;
  }, [
    enforceRequiredContact,
    clientContact,
    defendantsForAssign,
    requiredInsurancePartyContact,
  ]);

  const selectedInsuranceType = insuranceForm.watch("type") as
    | InsuranceType
    | undefined;
  const isAutoInsurance = selectedInsuranceType === "Auto";
  const supportsAdjusterRoles =
    isAutoInsurance || selectedInsuranceType === "Commercial";
  const coverageEnabled = insuranceForm.watch("coverageEnabled");
  const shouldDirtyDependentResets = insuranceForm.formState.isDirty;
  const medPayEnabled = insuranceForm.watch("medPay");
  const umEnabled = insuranceForm.watch("umExists");
  const uimEnabled = insuranceForm.watch("uimExists");
  const shouldShowMedPayAdjuster =
    isAutoInsurance && coverageEnabled && medPayEnabled;

  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const [isInsuranceCopyModeOpen, setIsInsuranceCopyModeOpen] = useState(false);
  const [selectedRelatedMatterIds, setSelectedRelatedMatterIds] = useState<
    string[]
  >([]);
  const relatedMatterOptions = useMemo(
    () => getRelatedMatterSelectionOptions(matter),
    [matter],
  );
  const canCopyToRelatedMatters =
    !editing &&
    Boolean(insurancePartyRole && sourceMatterContactConnectionId) &&
    relatedMatterOptions.length > 0 &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterInsurance,
      ACTION_PERMISSION_VALUES.create,
    );

  const insuranceModalFormId = "add-matter-insurance-modal-form";
  const lockInsuranceType =
    (editing && Boolean(insurance?.type)) ||
    (!editing && Boolean(initialData?.type));

  const onSubmit = async (
    data: z.infer<typeof insuranceFormSchema>,
    placements: InsuranceRelatedMatterPlacement[] = [],
    shouldCloseAfterSave = closeAfterSave,
  ) => {
    setIsSubmittingInsurance(true);
    let createdInsuranceId: string | undefined;
    try {
      const submissionData = omit(
        {
          ...data,
          category: data.category ?? "",
          coverage: data.coverage ?? "",
          liability: data.liability ?? "",
          // Clear MedPay if not selected
          medPayLimit: data.medPay ? data.medPayLimit : null,
          // Clear UM if not selected
          umPerPerson: data.umExists ? data.umPerPerson : null,
          umPerIncident: data.umExists ? data.umPerIncident : null,
          // Clear UIM if not selected
          uimPerPerson: data.uimExists ? data.uimPerPerson : null,
          uimPerIncident: data.uimExists ? data.uimPerIncident : null,
        },
        [
          "matterContactConnectionInsured",
          "matterContactConnectionInsurer",
          "matterContactConnectionPolicyHolder",
          "matterContactConnectionBodilyInjuryAdjuster",
          "matterContactConnectionPropertyDamageAdjuster",
          "matterContactConnectionMedPayAdjuster",
          "matterContactConnectionAdjusterSupervisor",
          "matterContactConnectionSubrogationVendor",
          "matterContactConnectionSubrogationRepresentative",
          "policyLimitCombined",
          "umuimCombined",
        ],
      );

      const selectedContactIdsByRole = {
        "Adjuster Supervisor": contactIds(
          data.matterContactConnectionAdjusterSupervisor?.id,
        ),
        "Bodily Injury Adjuster": contactIds(
          data.matterContactConnectionBodilyInjuryAdjuster?.id,
        ),
        Insured: data.matterContactConnectionInsured.map(
          (contact) => contact.id,
        ),
        Insurer: contactIds(data.matterContactConnectionInsurer?.id),
        "MedPay Adjuster": contactIds(
          data.matterContactConnectionMedPayAdjuster?.id,
        ),
        "Policy Holder": contactIds(
          data.matterContactConnectionPolicyHolder?.id,
        ),
        "Property Damage Adjuster": contactIds(
          data.matterContactConnectionPropertyDamageAdjuster?.id,
        ),
        "Subrogation Representative": contactIds(
          data.matterContactConnectionSubrogationRepresentative?.id,
        ),
        "Subrogation Vendor": contactIds(
          data.matterContactConnectionSubrogationVendor?.id,
        ),
      } satisfies Record<SupportedInsuranceContactRole, string[]>;

      let resData;
      if (editing && insurance) {
        const result = await updateInsurance.mutateAsync({
          id: insurance.id,
          matterId: matter.id,
          dateModified: insurance.dateModified,
          ...submissionData,
        });
        resData = result;
        const desiredContactIdsByRole = Object.fromEntries(
          Object.entries(selectedContactIdsByRole).map(
            ([role, selectedContactIds]) => {
              return [
                role,
                role === "Insured"
                  ? selectedContactIds
                  : getDesiredInsuranceContactIdsForSingleSlot(
                      insurance,
                      role as SupportedInsuranceContactRole,
                      selectedContactIds[0],
                    ),
              ];
            },
          ),
        );
        await syncInsuranceConnections.mutateAsync({
          currentInsurance: insurance,
          desiredContactIdsByRole,
          insuranceId: result.data.id,
          matterId: matter.id,
        });
        await queryClient.refetchQueries({
          queryKey: [queryKeys.matters, matter.id],
        });
        toast.success("Matter insurance updated successfully");
      } else {
        const connections = Object.entries(selectedContactIdsByRole).flatMap(
          ([role, contactIds]) =>
            contactIds.map((contactId) =>
              createInsuranceContactConnectionChildInput(
                contactId,
                role as SupportedInsuranceContactRole,
              ),
            ),
        );
        const result = await createInsurance.mutateAsync({
          matterId: matter.id,
          ...submissionData,
          connections,
        });
        resData = result;
        createdInsuranceId = result.data.id;
        await queryClient.refetchQueries({
          queryKey: [queryKeys.matters, matter.id],
        });

        // Preserve the created source as an edit if the later copy fails.
        updateSlideoverPayload("insurance", {
          editing: true,
          insuranceId: result.data.id,
          title: SLIDEOVER_TITLE.INSURANCE.UPDATE,
          description: SLIDEOVER_DESCRIPTION.INSURANCE.UPDATE,
        });

        afterCreateFn?.(result);
        toast.success("Matter insurance created successfully");
      }
      if (placements.length > 0) {
        if (!sourceMatterContactConnectionId) {
          throw new Error("Insurance copy placement is required.");
        }
        await copyPartyInsurance.mutateAsync({
          insuranceId: resData.data.id,
          sourceMatterContactConnectionId,
          sourceMatterId: matter.id,
          placements,
        });
      }
      if (shouldCloseAfterSave) {
        setOpen(false);
        insuranceForm.reset({
          ...insuranceDefaultFormValues,
          ...omitBy(resData.data, isNil),
          // Use submitted form values for contact slots — matter-derived `insured` / etc.
          // can still be stale until refetch completes after connection mutations.
          matterContactConnectionInsured: data.matterContactConnectionInsured,
          matterContactConnectionInsurer: data.matterContactConnectionInsurer,
          matterContactConnectionPolicyHolder:
            data.matterContactConnectionPolicyHolder,
          matterContactConnectionBodilyInjuryAdjuster:
            data.matterContactConnectionBodilyInjuryAdjuster,
          matterContactConnectionPropertyDamageAdjuster:
            data.matterContactConnectionPropertyDamageAdjuster,
          matterContactConnectionMedPayAdjuster:
            data.matterContactConnectionMedPayAdjuster,
          matterContactConnectionAdjusterSupervisor:
            data.matterContactConnectionAdjusterSupervisor,
          matterContactConnectionSubrogationVendor:
            data.matterContactConnectionSubrogationVendor,
          matterContactConnectionSubrogationRepresentative:
            data.matterContactConnectionSubrogationRepresentative,
        });
        setSelectedInsuranceId?.(resData.data.id);
        setSelectedInsurer(null);
        setSelectedPolicyHolder(null);
        setSelectedBodilyInjuryAdjuster(null);
        setSelectedPropertyDamageAdjuster(null);
        setSelectedMedPayAdjuster(null);
        setSelectedAdjusterSupervisor(null);
        setSelectedSubrogationVendor(null);
        setSelectedSubrogationRepresentative(null);
      } else {
        updateSlideoverPayload("insurance", {
          editing: true,
          insuranceId: resData.data.id,
          title: SLIDEOVER_TITLE.INSURANCE.UPDATE,
          description: SLIDEOVER_DESCRIPTION.INSURANCE.UPDATE,
        });
        insuranceForm.reset({
          ...insuranceDefaultFormValues,
          ...omitBy(resData.data, isNil),
          matterContactConnectionInsured: data.matterContactConnectionInsured,
          matterContactConnectionInsurer: data.matterContactConnectionInsurer,
          matterContactConnectionPolicyHolder:
            data.matterContactConnectionPolicyHolder,
          matterContactConnectionBodilyInjuryAdjuster:
            data.matterContactConnectionBodilyInjuryAdjuster,
          matterContactConnectionPropertyDamageAdjuster:
            data.matterContactConnectionPropertyDamageAdjuster,
          matterContactConnectionMedPayAdjuster:
            data.matterContactConnectionMedPayAdjuster,
          matterContactConnectionAdjusterSupervisor:
            data.matterContactConnectionAdjusterSupervisor,
          matterContactConnectionSubrogationVendor:
            data.matterContactConnectionSubrogationVendor,
          matterContactConnectionSubrogationRepresentative:
            data.matterContactConnectionSubrogationRepresentative,
        });
        setSelectedInsurer(null);
        setSelectedPolicyHolder(null);
        setSelectedBodilyInjuryAdjuster(null);
        setSelectedPropertyDamageAdjuster(null);
        setSelectedMedPayAdjuster(null);
        setSelectedAdjusterSupervisor(null);
        setSelectedSubrogationVendor(null);
        setSelectedSubrogationRepresentative(null);
      }
    } catch (error) {
      toastAPIErrorMessage(
        error as AxiosError<APIError>,
        createdInsuranceId
          ? "Matter insurance was created, but could not be copied"
          : editing
            ? "Error updating matter insurance"
            : "Error creating matter insurance",
      );
    } finally {
      setIsSubmittingInsurance(false);
    }
  };

  const handleUnlink = async () => {
    if (!editing || !insurance) return;
    await unlinkInsurance.mutateAsync({
      matterId: matter.id,
      objectId: insurance.id,
      objectType: "insurance",
    });
    setOpen(false);
  };

  useEffect(() => {
    const clearSelectedConnections = () => {
      setSelectedInsurer(null);
      setSelectedPolicyHolder(null);
      setSelectedBodilyInjuryAdjuster(null);
      setSelectedPropertyDamageAdjuster(null);
      setSelectedMedPayAdjuster(null);
      setSelectedAdjusterSupervisor(null);
      setSelectedSubrogationVendor(null);
      setSelectedSubrogationRepresentative(null);
    };
    if (!open) {
      setIsInsuranceCopyModeOpen(false);
      insuranceForm.reset();
      clearSelectedConnections();
      return;
    }
    insuranceForm.reset(matterSnapshotFormValuesRef.current());
    clearSelectedConnections();
  }, [open, editing, insuranceId, insuranceForm]);

  const isHealthInsurance = selectedInsuranceType === "Health";
  const isSubrogation = insuranceForm.watch("subrogation") === true;

  useEffect(() => {
    // MedPay, UM, and UIM coverage applies to all auto insurance policies.
    if (!coverageEnabled || isHealthInsurance || !isAutoInsurance) {
      insuranceForm.setValue("medPay", false, {
        shouldDirty: shouldDirtyDependentResets,
      });
      insuranceForm.setValue("umExists", false, {
        shouldDirty: shouldDirtyDependentResets,
      });
      insuranceForm.setValue("uimExists", false, {
        shouldDirty: shouldDirtyDependentResets,
      });
      insuranceForm.setValue("medPayLimit", null, {
        shouldDirty: shouldDirtyDependentResets,
      });
      insuranceForm.setValue("umPerPerson", null, {
        shouldDirty: shouldDirtyDependentResets,
      });
      insuranceForm.setValue("umPerIncident", null, {
        shouldDirty: shouldDirtyDependentResets,
      });
      insuranceForm.setValue("uimPerPerson", null, {
        shouldDirty: shouldDirtyDependentResets,
      });
      insuranceForm.setValue("uimPerIncident", null, {
        shouldDirty: shouldDirtyDependentResets,
      });
    }
  }, [
    coverageEnabled,
    isHealthInsurance,
    isAutoInsurance,
    insuranceForm,
    shouldDirtyDependentResets,
  ]);

  useEffect(() => {
    if (shouldShowMedPayAdjuster) {
      return;
    }
    const medPayFormValue = insuranceForm.getValues(
      "matterContactConnectionMedPayAdjuster",
    );
    if (medPayFormValue?.id || selectedMedPayAdjuster || medPayAdjuster) {
      insuranceForm.setValue(
        "matterContactConnectionMedPayAdjuster",
        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
        { shouldDirty: shouldDirtyDependentResets },
      );
      setSelectedMedPayAdjuster(null);
    }
  }, [
    shouldShowMedPayAdjuster,
    insuranceForm,
    selectedMedPayAdjuster,
    medPayAdjuster,
    shouldDirtyDependentResets,
  ]);

  useEffect(() => {
    if (
      editing ||
      !open ||
      !defaultAssignableInsured ||
      currentInsuredContactIds.length > 0
    ) {
      return;
    }

    if (insuranceForm.formState.isDirty) {
      insuranceForm.setValue(
        "matterContactConnectionInsured",
        [defaultAssignableInsured],
        { shouldDirty: false },
      );
    } else {
      insuranceForm.reset({
        ...insuranceForm.getValues(),
        matterContactConnectionInsured: [defaultAssignableInsured],
      });
    }
  }, [
    currentInsuredContactIds.length,
    defaultAssignableInsured,
    editing,
    insuranceForm,
    insuranceForm.formState.isDirty,
    open,
  ]);

  useEffect(() => {
    if (supportsAdjusterRoles) {
      return;
    }
    const adjusterFields: {
      formKey:
        | "matterContactConnectionBodilyInjuryAdjuster"
        | "matterContactConnectionPropertyDamageAdjuster"
        | "matterContactConnectionAdjusterSupervisor";
      setter: (value: Contact | null) => void;
    }[] = [
      {
        formKey: "matterContactConnectionBodilyInjuryAdjuster",
        setter: setSelectedBodilyInjuryAdjuster,
      },
      {
        formKey: "matterContactConnectionPropertyDamageAdjuster",
        setter: setSelectedPropertyDamageAdjuster,
      },
      {
        formKey: "matterContactConnectionAdjusterSupervisor",
        setter: setSelectedAdjusterSupervisor,
      },
    ];
    adjusterFields.forEach(({ formKey, setter }) => {
      const formValue = insuranceForm.getValues(formKey);
      if (formValue?.id) {
        insuranceForm.setValue(
          formKey,
          MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
          { shouldDirty: shouldDirtyDependentResets },
        );
      }
      setter(null);
    });
  }, [
    supportsAdjusterRoles,
    insuranceForm,
    setSelectedAdjusterSupervisor,
    setSelectedBodilyInjuryAdjuster,
    setSelectedPropertyDamageAdjuster,
    shouldDirtyDependentResets,
  ]);

  const categoryItems = selectedInsuranceType
    ? CATEGORIES_BY_INSURANCE_TYPE[selectedInsuranceType]
    : [];
  const displayTitle = selectedInsuranceType
    ? editing
      ? `Update ${selectedInsuranceType.toLowerCase()} insurance`
      : `Add ${selectedInsuranceType.toLowerCase()} insurance`
    : title;

  const location = useLocation();

  const isOnDefendantsPage = location.pathname.includes("/defendant");
  const isOnClientsPage = location.pathname.includes("/client");

  const insuranceModalContactPickerProps = {
    contactPickerPresentation: "modal" as const,
    hideCompactContactChrome: true,
  };

  const insuranceModalContactSidebar = (
    <div className="flex flex-col gap-3">
      {coverageEnabled ? (
        <>
          <div className="flex flex-col gap-2">
            <AddContactFormField
              {...insuranceModalContactPickerProps}
              contactSelectorSize="compact"
              label="Insurer"
              register={insuranceForm.register(
                "matterContactConnectionInsurer",
              )}
              setSelectedContact={setSelectedInsurer}
              defaultSearchObject={{
                organizationSubTypes: ["Insurance"],
              }}
              onDelete={() => {
                insuranceForm.setValue(
                  "matterContactConnectionInsurer",
                  MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                  { shouldDirty: true },
                );
              }}
              matterId={matter.id}
              defaultValues={{
                type: "organization",
                organizationSubTypes: ["Insurance"],
              }}
              defaultSearchField="name"
              renderSelectedContactPreview={(contact) =>
                insuranceMatterContactRolePreview(
                  contact,
                  matter.id,
                  "Insurer",
                  insurer,
                  () => {
                    insuranceForm.setValue(
                      "matterContactConnectionInsurer",
                      MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                      { shouldDirty: true },
                    );
                  },
                )
              }
            />
            <AddContactFormField
              {...insuranceModalContactPickerProps}
              contactSelectorSize="compact"
              label="Insured"
              register={insuranceForm.register(
                "matterContactConnectionInsured",
              )}
              allowMultiple
              onDelete={(contact) => {
                insuranceForm.setValue(
                  "matterContactConnectionInsured",
                  insuranceForm
                    .getValues("matterContactConnectionInsured")
                    .filter(
                      (insuredContact) => insuredContact.id !== contact.id,
                    ),
                  { shouldDirty: true },
                );
              }}
              matterId={matter.id}
              renderSelectedContactPreview={(contact) =>
                insuranceMatterContactRolePreview(
                  contact,
                  matter.id,
                  "Insured",
                  insuredConnections.find(
                    (connection) => connection.targetObjectId === contact.id,
                  ),
                  () => {
                    insuranceForm.setValue(
                      "matterContactConnectionInsured",
                      insuranceForm
                        .getValues("matterContactConnectionInsured")
                        .filter(
                          (insuredContact) => insuredContact.id !== contact.id,
                        ),
                      { shouldDirty: true },
                    );
                  },
                )
              }
            />
            <AddContactFormField
              {...insuranceModalContactPickerProps}
              contactSelectorSize="compact"
              label="Policy holder"
              register={insuranceForm.register(
                "matterContactConnectionPolicyHolder",
              )}
              setSelectedContact={setSelectedPolicyHolder}
              onDelete={() => {
                insuranceForm.setValue(
                  "matterContactConnectionPolicyHolder",
                  MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                  { shouldDirty: true },
                );
              }}
              showSelectClientButton={isOnClientsPage}
              client={clientContact}
              showSelectDefendantButton={
                defendantConnection.length > 0 && isOnDefendantsPage
              }
              defendants={defendantsForAssign}
              matterId={matter.id}
              renderSelectedContactPreview={(contact) =>
                insuranceMatterContactRolePreview(
                  contact,
                  matter.id,
                  "Policy Holder",
                  policyHolder,
                  () => {
                    insuranceForm.setValue(
                      "matterContactConnectionPolicyHolder",
                      MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                      { shouldDirty: true },
                    );
                  },
                )
              }
            />
            {supportsAdjusterRoles && (
              <AddContactFormField
                {...insuranceModalContactPickerProps}
                contactSelectorSize="compact"
                label="Bodily injury adjuster"
                register={insuranceForm.register(
                  "matterContactConnectionBodilyInjuryAdjuster",
                )}
                setSelectedContact={setSelectedBodilyInjuryAdjuster}
                defaultSearchObject={{
                  individualSubTypes: ["Insurance Adjuster"],
                }}
                onDelete={() => {
                  insuranceForm.setValue(
                    "matterContactConnectionBodilyInjuryAdjuster",
                    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                    { shouldDirty: true },
                  );
                }}
                matterId={matter.id}
                client={clientContact}
                defendants={defendantsForAssign}
                defaultValues={{
                  type: "individual",
                  individualSubTypes: ["Insurance Adjuster"],
                }}
                renderSelectedContactPreview={(contact) =>
                  insuranceMatterContactRolePreview(
                    contact,
                    matter.id,
                    "Bodily Injury Adjuster",
                    bodilyInjuryAdjuster,
                    () => {
                      insuranceForm.setValue(
                        "matterContactConnectionBodilyInjuryAdjuster",
                        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                        { shouldDirty: true },
                      );
                    },
                  )
                }
              />
            )}
            {supportsAdjusterRoles && (
              <AddContactFormField
                {...insuranceModalContactPickerProps}
                contactSelectorSize="compact"
                label="Property damage adjuster"
                register={insuranceForm.register(
                  "matterContactConnectionPropertyDamageAdjuster",
                )}
                setSelectedContact={setSelectedPropertyDamageAdjuster}
                defaultSearchObject={{
                  individualSubTypes: ["Insurance Adjuster"],
                }}
                onDelete={() => {
                  insuranceForm.setValue(
                    "matterContactConnectionPropertyDamageAdjuster",
                    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                    { shouldDirty: true },
                  );
                }}
                matterId={matter.id}
                client={clientContact}
                defendants={defendantsForAssign}
                defaultValues={{
                  type: "individual",
                  individualSubTypes: ["Insurance Adjuster"],
                }}
                renderSelectedContactPreview={(contact) =>
                  insuranceMatterContactRolePreview(
                    contact,
                    matter.id,
                    "Property Damage Adjuster",
                    propertyDamageAdjuster,
                    () => {
                      insuranceForm.setValue(
                        "matterContactConnectionPropertyDamageAdjuster",
                        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                        { shouldDirty: true },
                      );
                    },
                  )
                }
              />
            )}
            {shouldShowMedPayAdjuster && (
              <AddContactFormField
                {...insuranceModalContactPickerProps}
                contactSelectorSize="compact"
                label="MedPay adjuster"
                register={insuranceForm.register(
                  "matterContactConnectionMedPayAdjuster",
                )}
                setSelectedContact={setSelectedMedPayAdjuster}
                defaultSearchObject={{
                  individualSubTypes: ["Insurance Adjuster"],
                }}
                onDelete={() => {
                  insuranceForm.setValue(
                    "matterContactConnectionMedPayAdjuster",
                    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                    { shouldDirty: true },
                  );
                }}
                matterId={matter.id}
                client={clientContact}
                defendants={defendantsForAssign}
                defaultValues={{
                  type: "individual",
                  individualSubTypes: ["Insurance Adjuster"],
                }}
                renderSelectedContactPreview={(contact) =>
                  insuranceMatterContactRolePreview(
                    contact,
                    matter.id,
                    "MedPay Adjuster",
                    medPayAdjuster,
                    () => {
                      insuranceForm.setValue(
                        "matterContactConnectionMedPayAdjuster",
                        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                        { shouldDirty: true },
                      );
                    },
                  )
                }
              />
            )}
            {supportsAdjusterRoles && (
              <AddContactFormField
                {...insuranceModalContactPickerProps}
                contactSelectorSize="compact"
                label="Adjuster supervisor"
                register={insuranceForm.register(
                  "matterContactConnectionAdjusterSupervisor",
                )}
                setSelectedContact={setSelectedAdjusterSupervisor}
                defaultSearchObject={{
                  individualSubTypes: ["Insurance Adjuster"],
                }}
                onDelete={() => {
                  insuranceForm.setValue(
                    "matterContactConnectionAdjusterSupervisor",
                    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                    { shouldDirty: true },
                  );
                }}
                matterId={matter.id}
                client={clientContact}
                defendants={defendantsForAssign}
                defaultValues={{
                  type: "individual",
                  individualSubTypes: ["Insurance Adjuster"],
                }}
                renderSelectedContactPreview={(contact) =>
                  insuranceMatterContactRolePreview(
                    contact,
                    matter.id,
                    "Adjuster Supervisor",
                    adjusterSupervisor,
                    () => {
                      insuranceForm.setValue(
                        "matterContactConnectionAdjusterSupervisor",
                        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                        { shouldDirty: true },
                      );
                    },
                  )
                }
              />
            )}
            {isSubrogation && (
              <AddContactFormField
                {...insuranceModalContactPickerProps}
                contactSelectorSize="compact"
                label="Subrogation vendor"
                register={insuranceForm.register(
                  "matterContactConnectionSubrogationVendor",
                )}
                setSelectedContact={setSelectedSubrogationVendor}
                defaultSearchObject={{
                  individualSubTypes: ["Subrogation Vendor"],
                }}
                onDelete={() => {
                  insuranceForm.setValue(
                    "matterContactConnectionSubrogationVendor",
                    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                    { shouldDirty: true },
                  );
                }}
                matterId={matter.id}
                client={clientContact}
                defendants={defendantsForAssign}
                defaultSearchField="name"
                renderSelectedContactPreview={(contact) =>
                  insuranceMatterContactRolePreview(
                    contact,
                    matter.id,
                    "Subrogation Vendor",
                    subrogationVendor,
                    () => {
                      insuranceForm.setValue(
                        "matterContactConnectionSubrogationVendor",
                        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                        { shouldDirty: true },
                      );
                    },
                  )
                }
              />
            )}
            {isSubrogation && (
              <AddContactFormField
                {...insuranceModalContactPickerProps}
                contactSelectorSize="compact"
                label="Subrogation representative"
                register={insuranceForm.register(
                  "matterContactConnectionSubrogationRepresentative",
                )}
                setSelectedContact={setSelectedSubrogationRepresentative}
                defaultSearchObject={{
                  individualSubTypes: ["Subrogation Representative"],
                }}
                onDelete={() => {
                  insuranceForm.setValue(
                    "matterContactConnectionSubrogationRepresentative",
                    MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                    { shouldDirty: true },
                  );
                }}
                matterId={matter.id}
                client={clientContact}
                defendants={defendantsForAssign}
                renderSelectedContactPreview={(contact) =>
                  insuranceMatterContactRolePreview(
                    contact,
                    matter.id,
                    "Subrogation Representative",
                    subrogationRepresentative,
                    () => {
                      insuranceForm.setValue(
                        "matterContactConnectionSubrogationRepresentative",
                        MATTER_CONTACT_CONNECTION_DEFAULT_VALUES,
                        { shouldDirty: true },
                      );
                    },
                  )
                }
              />
            )}
          </div>
        </>
      ) : (
        <div>
          <AddContactFormField
            {...insuranceModalContactPickerProps}
            label="Uninsured"
            register={insuranceForm.register("matterContactConnectionInsured")}
            allowMultiple
            onDelete={(contact) => {
              insuranceForm.setValue(
                "matterContactConnectionInsured",
                insuranceForm
                  .getValues("matterContactConnectionInsured")
                  .filter((insuredContact) => insuredContact.id !== contact.id),
                { shouldDirty: true },
              );
            }}
            matterId={matter.id}
            renderSelectedContactPreview={(contact) =>
              insuranceMatterContactRolePreview(
                contact,
                matter.id,
                "Insured",
                insuredConnections.find(
                  (connection) => connection.targetObjectId === contact.id,
                ),
                () => {
                  insuranceForm.setValue(
                    "matterContactConnectionInsured",
                    insuranceForm
                      .getValues("matterContactConnectionInsured")
                      .filter(
                        (insuredContact) => insuredContact.id !== contact.id,
                      ),
                    { shouldDirty: true },
                  );
                },
              )
            }
          />
        </div>
      )}
    </div>
  );

  const insuranceScrollArea = (
    <div className="overflow-y-auto">
      <fieldset
        className={cn(slideoverFieldsetClassName, "my-0 p-4 sm:p-5")}
        disabled={
          !hasActionPermission(
            ACTION_PERMISSION_KEYS.matterInsurance,
            ACTION_PERMISSION_VALUES.update,
          )
        }
      >
        {lockInsuranceType && selectedInsuranceType ? (
          <input type="hidden" {...insuranceForm.register("type")} />
        ) : (
          <SimpleSelectFormField
            label="Type*"
            items={INSURANCE_TYPES}
            register={insuranceForm.register("type")}
            className="overflow-hidden [&>span]:block [&>span]:truncate"
            onChange={(value) => {
              if (!value) return;
              insuranceForm.setValue("type", value, {
                shouldValidate: true,
              });
              // Clear category when type changes
              insuranceForm.setValue("category", "", {
                shouldValidate: true,
              });
            }}
          />
        )}
        <input type="hidden" {...insuranceForm.register("coverageEnabled")} />
        {coverageEnabled ? (
          <>
            {categoryItems.length > 0 ? (
              <SimpleSelectFormField
                key={selectedInsuranceType ?? "none"}
                label="Category"
                register={insuranceForm.register("category")}
                items={categoryItems}
                placeholder="Select a category"
                formatItemLabel={(item) => item}
                clearable
                className="overflow-hidden [&>span]:block [&>span]:truncate"
              />
            ) : null}
            {isHealthInsurance && (
              <>
                <TextFormField
                  label="Group number"
                  register={insuranceForm.register("groupNumber")}
                />
                <TextFormField
                  label="Member number"
                  register={insuranceForm.register("memberNumber")}
                />
              </>
            )}
            {!isHealthInsurance && (
              <>
                <TextFormField
                  label="Policy number"
                  register={insuranceForm.register("policyNumber")}
                />
                <TextFormField
                  label="Claim number"
                  register={insuranceForm.register("claimNumber")}
                />
                <SimpleSelectFormField
                  label="Coverage status"
                  placeholder="Select coverage status"
                  register={insuranceForm.register("coverage")}
                  items={MATTER_INSURANCE_COVERAGE_STATUS_OPTIONS}
                  clearable
                  className="overflow-hidden [&>span]:block [&>span]:truncate"
                />
                <SimpleSelectFormField
                  label="Liability"
                  register={insuranceForm.register("liability")}
                  items={MATTER_INSURANCE_LIABILITY_OPTIONS}
                  placeholder="Select liability"
                  clearable
                  className="overflow-hidden [&>span]:block [&>span]:truncate"
                />
              </>
            )}
            {!isHealthInsurance &&
              ["Accepted", "Partially Accepted"].includes(
                insuranceForm.watch("liability") ?? "",
              ) && (
                <TextFormField
                  label="Liability percentage"
                  register={insuranceForm.register("liabilityPercentage")}
                  type="number"
                />
              )}
            {isAutoInsurance && !isHealthInsurance && (
              <div className="col-span-full flex h-[62px] items-center justify-start">
                <SwitchFormField
                  register={insuranceForm.register("medPay")}
                  label="MedPay"
                />
              </div>
            )}
            {isAutoInsurance && !isHealthInsurance && medPayEnabled && (
              <CurrencyFormField
                label="MedPay limit"
                register={insuranceForm.register("medPayLimit")}
              />
            )}
            {!isHealthInsurance && (
              <>
                <div className="flex items-center">
                  <SwitchFormField
                    register={insuranceForm.register("policyLimitConfirmation")}
                    label="Policy limit confirmed"
                  />
                </div>

                <SimpleSelectFormField
                  label="Policy limit"
                  placeholder="Select policy limit"
                  register={insuranceForm.register("policyLimitCombined")}
                  items={Object.keys(policyLimitMappings)}
                  onChange={(value) => {
                    if (!value || !(value in policyLimitMappings)) {
                      insuranceForm.setValue("policyLimitPerPerson", null);
                      insuranceForm.setValue("policyLimitPerIncident", null);
                      return;
                    }
                    const valueObj =
                      policyLimitMappings[
                        value as keyof typeof policyLimitMappings
                      ];
                    insuranceForm.setValue(
                      "policyLimitPerPerson",
                      valueObj.person ?? null,
                    );
                    insuranceForm.setValue(
                      "policyLimitPerIncident",
                      valueObj.incident ?? null,
                    );
                  }}
                  formatItemLabel={(item) => item}
                  clearable
                />
                {insuranceForm.watch("policyLimitCombined") === "Other" && (
                  <>
                    <CurrencyFormField
                      label="Policy limit per person"
                      register={insuranceForm.register("policyLimitPerPerson")}
                    />
                    <CurrencyFormField
                      label="Policy limit per incident"
                      register={insuranceForm.register(
                        "policyLimitPerIncident",
                      )}
                    />
                  </>
                )}
              </>
            )}

            {isAutoInsurance && !isHealthInsurance && (
              <div className="col-span-full flex h-[62px] items-center justify-start">
                <SwitchFormField
                  register={insuranceForm.register("umExists")}
                  label="UM"
                />
              </div>
            )}
            {isAutoInsurance && !isHealthInsurance && umEnabled && (
              <>
                <CurrencyFormField
                  label="UM per person"
                  register={insuranceForm.register("umPerPerson")}
                />
                <CurrencyFormField
                  label="UM per incident"
                  register={insuranceForm.register("umPerIncident")}
                />
              </>
            )}
            {isAutoInsurance && !isHealthInsurance && (
              <div className="col-span-full flex h-[62px] items-center justify-start">
                <SwitchFormField
                  register={insuranceForm.register("uimExists")}
                  label="UIM"
                />
              </div>
            )}
            {isAutoInsurance && !isHealthInsurance && uimEnabled && (
              <>
                <CurrencyFormField
                  label="UIM per person"
                  register={insuranceForm.register("uimPerPerson")}
                />
                <CurrencyFormField
                  label="UIM per incident"
                  register={insuranceForm.register("uimPerIncident")}
                />
              </>
            )}
            <div className="col-span-full flex h-[62px] items-center justify-start">
              <SwitchFormField
                register={insuranceForm.register("subrogation")}
                label="Subrogation"
              />
            </div>
            {isSubrogation && (
              <div className="col-span-1">
                <TextFormField
                  register={insuranceForm.register("subrogationFileNumber")}
                  label="Subrogation file number"
                />
              </div>
            )}
          </>
        ) : null}
      </fieldset>
    </div>
  );

  return (
    <>
      <Form {...insuranceForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={displayTitle}
          titleTrailing={
            editing && insurance ? (
              <LinkedMatterMembershipPopover
                canUnlink={hasActionPermission(
                  ACTION_PERMISSION_KEYS.matterInsurance,
                  ACTION_PERMISSION_VALUES.delete,
                )}
                currentMatterId={matter.id}
                enabled={open}
                objectId={insurance.id}
                objectType="insurance"
                onUnlink={() => {
                  setIsUnlinking(true);
                }}
              />
            ) : undefined
          }
          description={undefined}
          size="4xl"
          loading={isFormLoading}
          isDirty={insuranceForm.formState.isDirty}
          preview={insuranceModalContactSidebar}
          previewLabel={null}
          bodyFormId={insuranceModalFormId}
          bodyFormOnSubmit={insuranceForm.handleSubmit((data) =>
            onSubmit(data),
          )}
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                {editing &&
                  hasActionPermission(
                    ACTION_PERMISSION_KEYS.matterInsurance,
                    ACTION_PERMISSION_VALUES.delete,
                  ) && (
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={unlinkInsurance.isPending}
                      onClick={() => {
                        setIsDeleting(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <FormCancelButton
                  setOpen={setOpen}
                  isDirty={insuranceForm.formState.isDirty}
                />
                <FormSubmitButton
                  submitFormId={insuranceModalFormId}
                  title={
                    canCopyToRelatedMatters
                      ? "Save to this & related matters"
                      : editing
                        ? "Save"
                        : "Add insurance"
                  }
                  type={canCopyToRelatedMatters ? "button" : "submit"}
                  loading={isFormLoading}
                  submitFn={() => {
                    if (canCopyToRelatedMatters) {
                      const allRelatedMatterIds = relatedMatterOptions.map(
                        (option) => option.id,
                      );
                      setSelectedRelatedMatterIds(allRelatedMatterIds);
                      if (insurancePartyRole === "Client") {
                        void insuranceForm.handleSubmit(() => {
                          setIsInsuranceCopyModeOpen(true);
                        })();
                        return;
                      }
                      void insuranceForm.handleSubmit((data) =>
                        onSubmit(
                          data,
                          allRelatedMatterIds.map((matterId) => ({
                            matterId,
                            placement: "other_party",
                          })),
                          true,
                        ),
                      )();
                      return;
                    }
                    setCloseAfterSave(true);
                  }}
                  disabled={isFormLoading}
                  additionalActions={
                    canCopyToRelatedMatters
                      ? [
                          {
                            label: "Save to this matter only",
                            onSelect: () => {
                              void insuranceForm.handleSubmit((data) =>
                                onSubmit(data, [], true),
                              )();
                            },
                          },
                        ]
                      : undefined
                  }
                />
              </div>
            </div>
          }
        >
          {insuranceScrollArea}
        </AppModal>
      </Form>
      <InsuranceCopyModeModal
        destinations={relatedMatterOptions.filter(({ id }) =>
          selectedRelatedMatterIds.includes(id),
        )}
        open={isInsuranceCopyModeOpen}
        onOpenChange={setIsInsuranceCopyModeOpen}
        loading={isFormLoading}
        onSubmit={(placements) => {
          setIsInsuranceCopyModeOpen(false);
          void insuranceForm.handleSubmit((data) =>
            onSubmit(data, placements, true),
          )();
        }}
      />
      {insurance ? (
        <RelatedMatterObjectDeleteModal
          open={isDeleting && !!insurance}
          onOpenChange={setIsDeleting}
          currentMatterId={matter.id}
          objectId={insurance.id}
          objectLabel="Insurance"
          objectType="insurance"
          onCurrentMatterRemoved={() => {
            setOpen(false);
          }}
        />
      ) : null}
      <ActionConfirmationModal
        title="Unlink this insurance from the matter?"
        description="This removes the insurance from this matter only. If this is its last matter, the insurance will be deleted."
        buttonText="Unlink insurance"
        open={isUnlinking && !!insurance}
        setOpen={setIsUnlinking}
        afterActionFn={handleUnlink}
        isActionPending={unlinkInsurance.isPending}
      />
    </>
  );
};
